import { animate, animation, style, transition, trigger, useAnimation } from "@angular/animations";
import { DOCUMENT } from "@angular/common";
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ComponentRef, Directive, ElementRef, Inject, NgZone, Optional, PLATFORM_ID, Renderer2, SkipSelf, Type, ViewChild, ViewContainerRef, ViewEncapsulation, OnDestroy } from "@angular/core";
import { PrimeNGConfig } from "primeng/api";
import { DynamicDialogComponent, DynamicDialogConfig, DynamicDialogRef } from "primeng/dynamicdialog";
import { Nullable } from "primeng/ts-helpers";
import { LoadingService } from "../../services/loading.service";
import { ViewDialogInstance } from "./viewDialogService";

@Directive({
    selector: '[nxViewDialogDynamicContent]',
    host: {
        class: 'p-element'
    }
})
export class ViewDialogDynamicContentDirective {
    constructor(public viewContainerRef: ViewContainerRef) {}
}

const showAnimation = animation([style({ transform: '{{transform}}', opacity: 0 }), animate('{{transition}}', style({ transform: 'none', opacity: 1 }))]);
const hideAnimation = animation([animate('{{transition}}', style({ transform: '{{transform}}', opacity: 0 }))]);

@Component({
    selector: 'nx-viewDialog',
    template: `
        <div
            #mask
            [ngClass]="{
                'p-dialog-mask': true,
                'p-component-overlay p-component-overlay-enter p-dialog-mask-scrollblocker': config.modal !== false,
                'p-dialog-left': position === 'left',
                'p-dialog-right': position === 'right',
                'p-dialog-top': position === 'top',
                'p-dialog-bottom': position === 'bottom',
                'p-dialog-top-left': position === 'topleft' || position === 'top-left',
                'p-dialog-top-right': position === 'topright' || position === 'top-right',
                'p-dialog-bottom-left': position === 'bottomleft' || position === 'bottom-left',
                'p-dialog-bottom-right': position === 'bottomright' || position === 'bottom-right'
            }"
            [class]="config.maskStyleClass"
        >
            <div
                #container
                [ngClass]="{ 'p-dialog p-dynamic-dialog p-component': true, 'p-dialog-rtl': config.rtl, 'p-dialog-resizable': config.resizable, 'p-dialog-draggable': config.draggable, 'p-dialog-maximized': maximized }"
                [ngStyle]="config.style"
                [class]="config.styleClass"
                [@animation]="{ value: 'visible', params: { transform: transformOptions, transition: config.transitionOptions || '150ms cubic-bezier(0, 0, 0.2, 1)' } }"
                (@animation.start)="onAnimationStart($event)"
                (@animation.done)="onAnimationEnd($event)"
                role="dialog"
                *ngIf="visible"
                [style.width]="config.width"
                [style.height]="config.height"
                [attr.aria-labelledby]="ariaLabelledBy"
                [attr.aria-modal]="true"
            >
                <div *ngIf="config.resizable" class="p-resizable-handle" style="z-index: 90;" (mousedown)="initResize($event)"></div>
                <div #titlebar class="p-dialog-header" (mousedown)="initDrag($event)" *ngIf="config.showHeader === false ? false : true">
                    <span class="p-dialog-title" [id]="ariaLabelledBy + '_title'">{{ config.header | translate }}</span>
                    <div class="p-dialog-header-icons">
                        <div class="close" (click)="hide()">+</div>
                    </div>
                </div>
                <div #content class="p-dialog-content" [ngStyle]="config.contentStyle">
                    <ng-template nxViewDialogDynamicContent></ng-template>
                </div>
                <div class="p-dialog-footer flex flex-row justify-end">
                    <nx-default-button
                        class=""
                        [label]="closeLabel | translate"
                        [disabled]="loadingService?.isLoadingSubject?.getValue()"
                        (click)="close()">
                    </nx-default-button>
                </div>
            </div>
        </div>
    `,
    animations: [trigger('animation', [transition('void => visible', [useAnimation(showAnimation)]), transition('visible => void', [useAnimation(hideAnimation)])])],
    changeDetection: ChangeDetectionStrategy.Default,
    encapsulation: ViewEncapsulation.None,
    styleUrls: ['../editDialog/editDialog.component.scss'],
    host: {
        class: 'p-element nx-view-dialog nx-dialog'
    }
})
export class ViewDialogComponent<T> extends DynamicDialogComponent implements OnDestroy {

    viewDialogInstance: ViewDialogInstance;
    ownDialogRef: DynamicDialogRef;
    closeLabel: string;

    @ViewChild(ViewDialogDynamicContentDirective) dialogContent: Nullable<ViewDialogDynamicContentDirective>;

    constructor(
        @Inject(DOCUMENT) document: Document,
        @Inject(PLATFORM_ID) platformId: any,
        cd: ChangeDetectorRef,
        renderer: Renderer2,
        config: DynamicDialogConfig,
        dialogRef: DynamicDialogRef,
        zone: NgZone,
        primeNGConfig: PrimeNGConfig,
        @SkipSelf() @Optional() parentDialog: DynamicDialogComponent,
        public loadingService: LoadingService,
        private elementRef: ElementRef<HTMLElement>
    ) {
        super(document, platformId, cd, renderer, config, dialogRef, zone, primeNGConfig, parentDialog);
        this.closeLabel = (config as any)?.closeLabel || 'core.viewDialog.close';
        this.ownDialogRef = dialogRef;
    }

    loadChildComponent(componentType: Type<any>) {
        let viewContainerRef = this.dialogContent.viewContainerRef;
        viewContainerRef?.clear();
        this.componentRef = viewContainerRef?.createComponent(componentType) as ComponentRef<T>;

        // set the instance
        this.viewDialogInstance = (this.componentRef as ComponentRef<ViewDialogInstance>).instance;
    }

    ngOnDestroy(): void {
        super.ngOnDestroy()
    }

    /**
     * Called when the close button in the footer is clicked.
     * Sets visible=false to trigger the close animation.
     * Calls the onClose callback for cleanup.
     */
    close(){
        // Call the cleanup callback first
        this.viewDialogInstance?.onClose(this);
        // Set visible = false to trigger animation and close the dialog
        // This is inherited from DynamicDialogComponent
        super.close();
    }

    setAdditionalClass(className: string) {
        this.renderer.addClass(this.elementRef.nativeElement, className);
    }

}
